/**
 * Message Queue Dashboard Component
 *
 * Admin dashboard for monitoring pg-boss queue system:
 * - Dead Letter Queue (failed jobs)
 * - Message Queue (active/pending jobs)
 * - Successful Jobs (completed jobs)
 * - Scheduled Jobs (cron schedules)
 * - Job Registry (expected vs registered jobs)
 *
 * Epic: MVP 1.17 - Queue System, MVP 1.5.4 - Job Registry (SCRUM-1211)
 * Location: src/components/admin/MessageQueueDashboard.jsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layout/DashboardLayout';

import { apiRequest } from '../../services/api';
import {
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  PlayCircle,
  Calendar,
  ClipboardList,
  Play,
  RotateCcw
} from 'lucide-react';
import Alert from '../common/Alert';
import Badge from '../common/Badge';
import StatusBadge from '../common/StatusBadge';
import TabGroup from '../common/TabGroup';
import EmptyState from '../common/EmptyState';

const QUEUE_TABS = {
  deadLetters: { name: 'Dead Letter Queue', icon: XCircle, color: 'red' },
  active: { name: 'Message Queue', icon: PlayCircle, color: 'blue' },
  completed: { name: 'Successful Jobs', icon: CheckCircle, color: 'green' },
  scheduled: { name: 'Scheduled Jobs', icon: Calendar, color: 'purple' },
  registry: { name: 'Job Registry', icon: ClipboardList, color: 'orange' }
};

const MessageQueueDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('deadLetters');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [registrySummary, setRegistrySummary] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let endpoint;
      let dataKey;

      switch (activeTab) {
        case 'deadLetters':
          endpoint = '/api/v1/queue/dead-letters';
          dataKey = 'deadLetters';
          break;
        case 'active':
          endpoint = '/api/v1/queue/active';
          dataKey = 'jobs';
          break;
        case 'completed':
          endpoint = '/api/v1/queue/completed';
          dataKey = 'jobs';
          break;
        case 'scheduled':
          endpoint = '/api/v1/queue/scheduled';
          dataKey = 'schedules';
          break;
        case 'registry': {
          const registryData = await apiRequest('/api/v1/admin/jobs/registry');
          setJobs(registryData.data?.jobs || []);
          setRegistrySummary(registryData.data?.summary || null);
          return;
        }
        default:
          endpoint = '/api/v1/queue/dead-letters';
          dataKey = 'deadLetters';
      }

      const data = await apiRequest(endpoint);
      setJobs(data[dataKey] || []);
      setRegistrySummary(null);
    } catch (err) {
      // Error already logged by apiRequest's errorLoggingService
      setError(err.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Auto-refresh for registry tab (60 seconds)
  useEffect(() => {
    if (activeTab !== 'registry') {return;}
    const interval = setInterval(fetchJobs, 60000);
    return () => clearInterval(interval);
  }, [activeTab, fetchJobs]);

  const toggleJobExpansion = (jobId) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) {return 'N/A';}
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getJobTypeBadgeColor = (jobName) => {
    if (!jobName) {return 'gray';}
    if (jobName.startsWith('email')) {return 'blue';}
    if (jobName.startsWith('sms')) {return 'purple';}
    if (jobName.startsWith('cleanup')) {return 'green';}
    if (jobName.startsWith('analytics')) {return 'orange';}
    if (jobName.startsWith('social-proof')) {return 'pink';}
    if (jobName.startsWith('seo')) {return 'indigo';}
    if (jobName.startsWith('credential')) {return 'yellow';}
    if (jobName.startsWith('license')) {return 'green';}
    return 'gray';
  };

  const getStateVariant = (state) => {
    switch (state) {
      case 'created':
        return 'info';
      case 'retry':
        return 'warning';
      case 'active':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'neutral';
    }
  };

  const getRegistryStatusBadge = (status) => {
    switch (status) {
      case 'registered':
        return <StatusBadge variant="success">Registered</StatusBadge>;
      case 'missing':
        return <StatusBadge variant="error">Missing</StatusBadge>;
      case 'conditional-missing':
        return <StatusBadge variant="warning">Conditional</StatusBadge>;
      case 'event-driven':
        return <StatusBadge variant="neutral">Event-driven</StatusBadge>;
      default:
        return <StatusBadge variant="neutral">{status}</StatusBadge>;
    }
  };

  const handleReRegister = async (jobName) => {
    setActionLoading(jobName);
    try {
      await apiRequest(`/api/v1/admin/jobs/${jobName}/register`, { method: 'POST' });
      await fetchJobs();
    } catch (err) {
      setError(`Failed to re-register ${jobName}: ${err.message}`);
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const handleRunNow = async (jobName) => {
    setActionLoading(jobName);
    try {
      await apiRequest(`/api/v1/admin/jobs/${jobName}/run`, { method: 'POST' });
      await fetchJobs();
    } catch (err) {
      setError(`Failed to run ${jobName}: ${err.message}`);
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const getCurrentTabInfo = () => {
    return QUEUE_TABS[activeTab];
  };

  const currentTab = getCurrentTabInfo();
  const IconComponent = currentTab.icon;

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="queue"
      onNavigate={handleNavigate}
      user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-6)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>Message Queue Monitor</h1>
            <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-2)' }}>Monitor job queue health and execution</p>
          </div>
          <button
            onClick={fetchJobs}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <TabGroup
          tabs={Object.entries(QUEUE_TABS).map(([key, { name }]) => ({
            id: key,
            label: name,
          }))}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        {/* Error Alert */}
        {error && (
          <Alert variant="error" message={`Error Loading Data: ${error}`} />
        )}

        {/* Confirmation Modal */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmAction.type === 'register' ? 'Re-register Job' : 'Run Job Now'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {confirmAction.type === 'register'
                  ? `Re-register "${confirmAction.jobName}"? This registers the job with pg-boss. Does not trigger an immediate run.`
                  : `Trigger immediate run of "${confirmAction.jobName}"? This enqueues the job outside its normal schedule.`
                }
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmAction.type === 'register'
                    ? handleReRegister(confirmAction.jobName)
                    : handleRunNow(confirmAction.jobName)
                  }
                  disabled={actionLoading === confirmAction.jobName}
                  className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${
                    confirmAction.type === 'register'
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {actionLoading === confirmAction.jobName ? 'Processing...' :
                    confirmAction.type === 'register' ? 'Re-register' : 'Run Now'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <IconComponent className={`w-6 h-6 text-${currentTab.color}-600`} />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{currentTab.name}</h2>
                <p className="text-sm text-gray-600">
                  {activeTab === 'deadLetters' && 'Failed jobs that need attention'}
                  {activeTab === 'active' && 'Currently pending or executing jobs'}
                  {activeTab === 'completed' && 'Successfully completed jobs'}
                  {activeTab === 'scheduled' && 'Cron-scheduled recurring jobs'}
                  {activeTab === 'registry' && 'Expected vs registered job status'}
                </p>
              </div>
              {!loading && (
                <span className={`ml-auto bg-${currentTab.color}-100 text-${currentTab.color}-800 text-sm font-medium px-3 py-1 rounded-full`}>
                  {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                </span>
              )}
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Loading jobs...</p>
              </div>
            ) : jobs.length === 0 ? (
              <EmptyState
                icon={IconComponent}
                title={
                  activeTab === 'deadLetters' ? 'All Clear!' :
                  activeTab === 'active' ? 'No Active Jobs' :
                  activeTab === 'completed' ? 'No Completed Jobs' :
                  activeTab === 'registry' ? 'No Registry Data' :
                  'No Scheduled Jobs'
                }
                description={
                  activeTab === 'deadLetters' ? 'No failed jobs in the queue' :
                  activeTab === 'active' ? 'No jobs currently pending or executing' :
                  activeTab === 'completed' ? 'No completed jobs found' :
                  activeTab === 'registry' ? 'Could not load job registry data' :
                  'No cron schedules configured'
                }
              />
            ) : activeTab === 'registry' ? (
              /* Job Registry Tab */
              <div>
                {/* Summary Line */}
                {registrySummary && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700" data-testid="registry-summary">
                    <span className="font-medium">{registrySummary.registered}</span> of{' '}
                    <span className="font-medium">{registrySummary.total}</span> expected jobs registered.
                    {registrySummary.missing > 0 && (
                      <span className="text-red-600 font-medium ml-2">
                        {registrySummary.missing} missing.
                      </span>
                    )}
                    {registrySummary.conditionalMissing > 0 && (
                      <span className="text-amber-600 ml-2">
                        {registrySummary.conditionalMissing} conditional.
                      </span>
                    )}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Job Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Schedule
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Run
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {jobs.map((job, index) => (
                        <tr key={`${job.name}-${index}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-mono text-sm text-gray-900">{job.name}</div>
                            {job.description && (
                              <div className="text-xs text-gray-500 mt-1">{job.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {job.schedule ? (
                              <span className="text-sm font-mono text-gray-900">{job.schedule}</span>
                            ) : (
                              <span className="text-sm text-gray-500 italic">Event-driven</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge color={job.tier === 'critical' ? 'red' : 'gray'}>
                              {job.tier}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-testid={`status-${job.name}`}>
                            {getRegistryStatusBadge(job.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {job.lastRun ? formatDate(job.lastRun) : 'Never'}
                            {(job.executions24h > 0 || job.failures24h > 0) && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex items-center">
                                  <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
                                  <span className="text-xs">{job.executions24h}</span>
                                </div>
                                {job.failures24h > 0 && (
                                  <div className="flex items-center">
                                    <XCircle className="w-3 h-3 text-red-500 mr-1" />
                                    <span className="text-xs text-red-600">{job.failures24h}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {job.status === 'missing' && !job.conditional && (
                                <button
                                  onClick={() => setConfirmAction({ type: 'register', jobName: job.name })}
                                  disabled={actionLoading === job.name}
                                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-full hover:bg-orange-200 disabled:opacity-50"
                                  data-testid={`reregister-${job.name}`}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                  Re-register
                                </button>
                              )}
                              {job.status === 'registered' && job.schedule !== null && (
                                <button
                                  onClick={() => setConfirmAction({ type: 'run', jobName: job.name })}
                                  disabled={actionLoading === job.name}
                                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full hover:bg-blue-200 disabled:opacity-50"
                                  data-testid={`runnow-${job.name}`}
                                >
                                  <Play className="w-3 h-3" />
                                  Run Now
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : activeTab === 'scheduled' ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Schedule
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timezone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Run
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        24h Stats
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs.map((schedule, index) => (
                      <tr key={`${schedule.name}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Badge color={getJobTypeBadgeColor(schedule.name)}>
                              {schedule.name}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">{schedule.cron || 'N/A'}</div>
                          <div className="text-xs text-gray-500">
                            {schedule.cron === '* * * * *' && 'Every minute'}
                            {schedule.cron === '*/2 * * * *' && 'Every 2 minutes'}
                            {schedule.cron === '*/15 * * * *' && 'Every 15 minutes'}
                            {schedule.cron === '0 * * * *' && 'Every hour'}
                            {schedule.cron === '0 0,6,12,18 * * *' && 'Every 6 hours'}
                            {schedule.cron && schedule.cron.startsWith('0 ') && schedule.cron.split(' ').length === 5 && !schedule.cron.includes(',') && 'Daily'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {schedule.timezone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {schedule.last_run ? formatDate(schedule.last_run) : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                              <span className="text-sm text-gray-900">{schedule.executions_24h}</span>
                            </div>
                            {schedule.failures_24h > 0 && (
                              <div className="flex items-center">
                                <XCircle className="w-4 h-4 text-red-500 mr-1" />
                                <span className="text-sm text-red-600">{schedule.failures_24h}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.map((job) => {
                  const isExpanded = expandedJobs.has(job.id);

                  return (
                    <div key={job.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Job Header */}
                      <div
                        className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => toggleJobExpansion(job.id)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge color={getJobTypeBadgeColor(job.name)}>
                                {job.name}
                              </Badge>
                              {job.state && (
                                <StatusBadge variant={getStateVariant(job.state)}>
                                  {job.state}
                                </StatusBadge>
                              )}
                              <span className="text-xs text-gray-500">
                                ID: {job.id ? job.id.substring(0, 8) : 'N/A'}...
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>
                                  {activeTab === 'deadLetters' && `Failed: ${formatDate(job.completed_on)}`}
                                  {activeTab === 'active' && `Created: ${formatDate(job.created_on)}`}
                                  {activeTab === 'completed' && `Completed: ${formatDate(job.completed_on)}`}
                                </span>
                              </div>
                              {activeTab === 'deadLetters' && job.output?.error && (
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="w-4 h-4" />
                                  <span className="font-medium text-red-600">
                                    {job.output.error}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <button className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 border-t border-gray-200 bg-white space-y-4">
                          {/* Job Metadata */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Priority:</span>
                              <span className="ml-2 text-gray-600">{job.priority || 'Normal'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Retry Count:</span>
                              <span className="ml-2 text-gray-600">{job.retry_count || 0}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Created:</span>
                              <span className="ml-2 text-gray-600">{formatDate(job.created_on)}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Started:</span>
                              <span className="ml-2 text-gray-600">{formatDate(job.started_on)}</span>
                            </div>
                            {activeTab !== 'active' && (
                              <div>
                                <span className="font-medium text-gray-700">Completed:</span>
                                <span className="ml-2 text-gray-600">{formatDate(job.completed_on)}</span>
                              </div>
                            )}
                          </div>

                          {/* Job Data */}
                          {job.data && (
                            <div>
                              <h4 className="font-medium text-gray-700 mb-2">Job Data:</h4>
                              <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto">
                                {JSON.stringify(job.data, null, 2)}
                              </pre>
                            </div>
                          )}

                          {/* Error Details (for failed jobs) */}
                          {activeTab === 'deadLetters' && job.output && (
                            <div>
                              <h4 className="font-medium text-gray-700 mb-2">Error Details:</h4>
                              <pre className="bg-red-50 border border-red-200 rounded p-3 text-xs overflow-x-auto text-red-900">
                                {JSON.stringify(job.output, null, 2)}
                              </pre>
                            </div>
                          )}

                          {/* Output (for completed jobs) */}
                          {activeTab === 'completed' && job.output && (
                            <div>
                              <h4 className="font-medium text-gray-700 mb-2">Job Output:</h4>
                              <pre className="bg-green-50 border border-green-200 rounded p-3 text-xs overflow-x-auto text-green-900">
                                {JSON.stringify(job.output, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MessageQueueDashboard;
